import { Fragment, useMemo } from 'react'

import { useUnit } from 'effector-react'

import CalendarIcon from '@/assets/icons/icon-calendar.svg'
import EmojiIcon from '@/assets/icons/icon-emoji.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmojiPickerPopover } from '@/components/ui/emoji-picker-popover'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { $hasEditVaultPermission } from '@/models/permissions.ts'
import TagIcon from '@/modules/chatting/icons/@.svg'
import ActionAudioIcon from '@/modules/chatting/icons/action-audio.svg'
import ActionCountdownIcon from '@/modules/chatting/icons/action-countdown.svg'
import GifActionIcon from '@/modules/chatting/icons/action-gif.svg'
import ActionPriceTag from '@/modules/chatting/icons/action-price-tag.svg'
import VaultActionIcon from '@/modules/chatting/icons/action-vault.svg'
import GalleryIcon from '@/modules/chatting/icons/gallery.svg'
import TextFormatIcon from '@/modules/chatting/icons/icon-text-format.svg'
import MoreIcon from '@/modules/chatting/icons/more.svg'

import { IconButton } from '../icon-button'
import { VerticalSeparator } from '../vertical-separator'
import { GifsList } from './GifsList/GifsList'
import { OverflowingChatMessageActionItem } from './OverflowingChatMessageActionItem'
import { ScheduleMessage } from './ScheduleMessage'
import { ACCEPTED_MEDIA_MIME_TYPES } from './consts'
import { ActionItem, ChattingMessageEditorActionsProps } from './types'
import { useChattingMessageEditorActions } from './useChattingMessageEditorActions'
import { useOverflowingActions } from './useOverflowingActions'

export const ChattingMessageEditorActions = (
  props: ChattingMessageEditorActionsProps,
) => {
  const hasEditVaultPermission = useUnit($hasEditVaultPermission)
  const {
    isCountdownIncluded,
    fileInputRef,
    scheduledDate,
    setScheduledDate,
    onClickAudio,
    onClickPrice,
    onClickFormat,
    scheduleDisabled,
  } = props
  const {
    handleFileInputChange,
    handleVaultOpen,
    handleEmojiClick,
    handleClickTag,
    handleClickCountdown,
    handleClickGif,
    handleSelectGif,
    handleClickOverflowedSchedule,
    handleClickOverflowedEmojis,
    handleClickOverflowedGif,
    onGifsListOpenChange,
    setIsDetachedSchedulePopoverOpen,
    setIsDetachedEmojisPopoverOpen,
    canMediaBeAdded,
    canGifBeAdded,
    canPriceBeAdded,
    isGifsListOpen,
    isDetachedSchedulePopoverOpen,
    isDetachedEmojisPopoverOpen,
  } = useChattingMessageEditorActions(props)

  const actions: ActionItem[] = useMemo(
    () =>
      [
        hasEditVaultPermission && {
          name: 'addMedia',
          defaultView: (
            <IconButton
              iconClassName="h-6 w-6"
              icon={GalleryIcon}
              tooltip="Add media"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canMediaBeAdded}
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={GalleryIcon}
              label="Add media"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canMediaBeAdded}
            />
          ),
        },
        {
          name: 'gif',
          defaultView: (
            <IconButton
              icon={GifActionIcon}
              tooltip="Add gif"
              disabled={!canGifBeAdded}
              onClick={handleClickGif}
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={GifActionIcon}
              label="Add gif"
              disabled={!canGifBeAdded}
              onClick={handleClickOverflowedGif}
            />
          ),
        },
        {
          name: 'vault',
          defaultView: (
            <IconButton
              icon={VaultActionIcon}
              onClick={handleVaultOpen}
              tooltip="Add media from vault"
              disabled={!canMediaBeAdded}
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={VaultActionIcon}
              label="Add media from vault"
              disabled={!canMediaBeAdded}
              onClick={handleVaultOpen}
            />
          ),
        },
        hasEditVaultPermission && {
          name: 'audio',
          withSeparator: true,
          defaultView: (
            <IconButton
              icon={ActionAudioIcon}
              tooltip="Add audio"
              onClick={onClickAudio}
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={ActionAudioIcon}
              label="Add audio"
              onClick={onClickAudio}
            />
          ),
        },
        {
          name: 'emoji',
          withSeparator: true,
          defaultView: (
            <EmojiPickerPopover
              tooltip="Emojis"
              basicIconStyle
              popoverSide="top"
              onEmojiClick={handleEmojiClick}
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={EmojiIcon}
              label="Emojis"
              onClick={handleClickOverflowedEmojis}
            />
          ),
        },
        {
          name: 'schedule',
          withSeparator: true,
          defaultView: (
            <ScheduleMessage
              scheduledDate={scheduledDate}
              setScheduledDate={setScheduledDate}
              disabled={scheduleDisabled}
              disabledTooltip="Scheduled messages are unavailable while sending a PMM."
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={CalendarIcon}
              label="Scheduled Messages"
              onClick={
                scheduleDisabled ? undefined : handleClickOverflowedSchedule
              }
              disabled={scheduleDisabled}
            />
          ),
        },
        {
          name: 'tagCreator',
          defaultView: (
            <IconButton
              icon={TagIcon}
              tooltip="Tag other creators"
              onClick={handleClickTag}
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={TagIcon}
              label="Tag other creators"
              onClick={handleClickTag}
            />
          ),
        },
        {
          name: 'price',
          defaultView: (
            <IconButton
              icon={ActionPriceTag}
              onClick={onClickPrice}
              tooltip="Message price"
              disabled={!canPriceBeAdded}
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={ActionPriceTag}
              label="Message price"
              onClick={onClickPrice}
              disabled={!canPriceBeAdded}
            />
          ),
        },
        isCountdownIncluded && {
          name: 'countdown',
          defaultView: (
            <IconButton
              tooltip="Countdown"
              icon={ActionCountdownIcon}
              onClick={handleClickCountdown}
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={ActionCountdownIcon}
              label="Countdown"
              onClick={handleClickCountdown}
            />
          ),
        },
        {
          name: 'format',
          defaultView: (
            <IconButton
              onClick={onClickFormat}
              icon={TextFormatIcon}
              tooltip="Format Text"
            />
          ),
          overflowingView: (
            <OverflowingChatMessageActionItem
              icon={TextFormatIcon}
              label="Format Text"
              onClick={onClickFormat}
            />
          ),
        },
      ].filter((actionItem): actionItem is ActionItem => Boolean(actionItem)),
    [
      hasEditVaultPermission,
      canMediaBeAdded,
      canGifBeAdded,
      handleClickGif,
      handleClickOverflowedGif,
      handleVaultOpen,
      onClickAudio,
      handleEmojiClick,
      handleClickOverflowedEmojis,
      scheduledDate,
      setScheduledDate,
      handleClickOverflowedSchedule,
      handleClickTag,
      onClickPrice,
      canPriceBeAdded,
      isCountdownIncluded,
      handleClickCountdown,
      onClickFormat,
      fileInputRef,
      scheduleDisabled,
    ],
  )

  const { visible, overflowing, containerRef } = useOverflowingActions({
    actions,
  })

  return (
    <div
      className="flex text-secondary-300 flex-1 overflow-hidden"
      ref={containerRef}
    >
      {hasEditVaultPermission && (
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileInputChange}
          className="hidden"
          accept={ACCEPTED_MEDIA_MIME_TYPES}
        />
      )}

      {/** Detached popovers to open from overflowing actions dropdown */}
      <Popover
        open={isGifsListOpen}
        onOpenChange={onGifsListOpenChange}
      >
        <PopoverTrigger asChild>
          <div />
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="start"
          className="w-[100vw] max-w-[624px] p-4 z-[70]"
        >
          <GifsList onClickGif={handleSelectGif} />
        </PopoverContent>
      </Popover>
      <ScheduleMessage
        scheduledDate={scheduledDate}
        setScheduledDate={setScheduledDate}
        open={isDetachedSchedulePopoverOpen}
        onOpenChange={setIsDetachedSchedulePopoverOpen}
        trigger={<div />}
        disabled={scheduleDisabled}
        disabledTooltip="Scheduled messages are unavailable while sending a PMM."
      />
      <EmojiPickerPopover
        trigger={<div />}
        open={isDetachedEmojisPopoverOpen}
        onOpenChange={setIsDetachedEmojisPopoverOpen}
        popoverSide="top"
        onEmojiClick={handleEmojiClick}
      />

      <div className="flex items-center gap-3">
        {visible.map(({ name, defaultView, withSeparator }) => (
          <Fragment key={name}>
            {withSeparator && <VerticalSeparator className="h-6" />}
            {defaultView}
          </Fragment>
        ))}
        {overflowing.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger className="outline-none">
              <IconButton
                icon={MoreIcon}
                iconClassName="rotate-90"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="p-2"
              // To fix overlaying issue in new message dialog
              // eslint-disable-next-line no-inline-styles/no-inline-styles
              style={{ zIndex: 100 }}
            >
              {overflowing.map(({ name, overflowingView }) => (
                <Fragment key={name}>{overflowingView}</Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
